import Logo from '../components/Logo';
import AccessForm from '../components/AccessForm';

function PreviewMock() {
  return (
    <div className="preview" aria-hidden="true">
      <div className="preview-window">
        <div className="preview-chrome">
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
          <span className="preview-url">sendoc.co/view/…</span>
        </div>
        <div className="preview-body">
          <div className="preview-slide">
            <div className="preview-slide-kicker" />
            <div className="preview-slide-title" />
            <div className="preview-slide-line" />
            <div className="preview-slide-line short" />
            <div className="preview-slide-chart">
              <span style={{ height: '34%' }} />
              <span style={{ height: '52%' }} />
              <span style={{ height: '44%' }} />
              <span style={{ height: '70%' }} />
              <span style={{ height: '88%' }} />
            </div>
          </div>
          <div className="preview-sidebar">
            <div className="preview-sidebar-heading" />
            {[88, 64, 47, 31].map((w, i) => (
              <div className="preview-stat" key={i}>
                <span className="preview-stat-bar" style={{ width: `${w}%` }} />
                <span className="preview-stat-label" />
              </div>
            ))}
          </div>
        </div>
        <div className="preview-footer">
          <span className="preview-pager">‹ Page 3 of 12 ›</span>
          <span className="preview-badge">Page 3 · 42s read</span>
        </div>
      </div>
    </div>
  );
}

const STEPS = [
  {
    title: 'Upload your deck',
    body: 'Drop in a PDF. Each page is rendered to a crisp image on our side — the original file is never sent to viewers.',
  },
  {
    title: 'Share one link',
    body: 'Send a single link by email or chat. Recipients read in their browser on any device — no app, no download, no attachment.',
  },
  {
    title: 'Read the room',
    body: 'See which pages held attention and which were skipped, per visit. Revoke the link the moment the deal moves on.',
  },
] as const;

const FEATURES = [
  {
    title: 'Originals stay private',
    body: 'Viewers receive pre-rendered page images over expiring signed URLs. There is no download button and no path to the source file.',
  },
  {
    title: 'Per-page reading time',
    body: 'Foreground time is measured per page with heartbeats and inactivity pauses — so the numbers reflect real reading, not an open tab.',
  },
  {
    title: 'Revocable links',
    body: 'Turn a link off at any time. Revoked shares show a clear, honest state instead of the document.',
  },
  {
    title: 'Works everywhere',
    body: 'Responsive viewer with keyboard, swipe and pinch zoom — from a phone in a taxi to a projector in a boardroom.',
  },
  {
    title: 'Accessible by default',
    body: 'Every page can carry an extracted text layer for screen readers, alongside full keyboard navigation.',
  },
  {
    title: 'Quiet by design',
    body: 'No pings, no read-receipt emails, no notifications to the viewer. Analytics are visible to you alone.',
  },
] as const;

export default function Landing() {
  return (
    <div className="landing">
      <header className="site-header">
        <div className="container site-header-inner">
          <a className="brand" href="/" aria-label="Sendoc home">
            <Logo />
          </a>
          <nav className="site-nav" aria-label="Primary">
            <a href="#how">How it works</a>
            <a href="#features">Features</a>
            <a className="btn btn-primary btn-sm" href="#access">
              Request access
            </a>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="container hero-inner">
            <div className="hero-copy">
              <p className="eyebrow">Document sharing, measured</p>
              <h1>
                Share documents.
                <br />
                See what's read.
              </h1>
              <p className="lede">
                Sendoc turns decks into secure, trackable links. Viewers read
                in the browser — the original file never leaves your hands — and
                you see per-page attention for every visit.
              </p>
              <div className="hero-ctas">
                <a className="btn btn-primary" href="#access">
                  Request access
                </a>
                <a className="btn btn-ghost" href="#how">
                  See how it works
                </a>
              </div>
            </div>
            <PreviewMock />
          </div>
        </section>

        <section className="section" id="how">
          <div className="container">
            <h2 className="section-title">How it works</h2>
            <ol className="steps">
              {STEPS.map((step, i) => (
                <li className="step" key={step.title}>
                  <span className="step-number" aria-hidden="true">
                    {i + 1}
                  </span>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="section section-alt" id="features">
          <div className="container">
            <h2 className="section-title">Built for documents that matter</h2>
            <div className="features">
              {FEATURES.map((feature) => (
                <article className="feature" key={feature.title}>
                  <h3>{feature.title}</h3>
                  <p>{feature.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="access">
          <div className="container access">
            <div className="access-copy">
              <h2 className="section-title">Request access</h2>
              <p>
                Sendoc is opening gradually. Tell us who you are and where
                you work, and we'll follow up when a seat is ready.
              </p>
            </div>
            <AccessForm />
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container site-footer-inner">
          <Logo size={22} />
          <p>© {new Date().getFullYear()} Sendoc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
